import { formatPrice } from '@ynap/intl-formatters'
import getOrderStatus from './getOrderStatus'
import getFormattedDate from 'app/utils/getFormattedDate'
import type { RawOrder, RawPendingOrder, Order, PendingOrder } from '../../../types/orders'

function getItemsWithPagination(orders, pageSize, messages, locale, config) {
    if (!orders) {
        return {
            items: undefined,
            pagination: {
                totalOrdersDisplayed: 0,
                totalOrders: 0,
                shouldDisplayLoadMore: false,
                currentPage: undefined,
            }
        }
    }
    const { items, pendingItems, recordSetTotal = 0 } = orders
    const processedOrders = getProcessedOrders(items, messages, locale, config)
    const processedPendingOrders = getProcessedPendingOrders(pendingItems, locale, config)

    // Count of orders already fetched from API
    const ordersCount = processedOrders.filter((o) => o.externalOrderID).length
    const pendingOrdersCount = processedPendingOrders.filter((o) => o.displayId).length
    const ordersWithPendingCount = ordersCount + pendingOrdersCount

    return {
        items: [...processedPendingOrders, ...processedOrders],
        pagination: {
            totalOrdersDisplayed: ordersWithPendingCount,
            totalOrders: parseInt(recordSetTotal) + pendingOrdersCount,
            shouldDisplayLoadMore: parseInt(recordSetTotal) - ordersCount !== 0,
            currentPage: Math.ceil(ordersCount / pageSize),
        }
    }
}

export default function getOrderHistory(state, props) {
    const { pageSize, messages } = props
    const {
        orderHistory,
        isFetchingOrderHistory,
        isFetchingPastOrderHistory,
        fetchOrderHistoryError,
        fetchPastOrderHistoryError,
        locale,
        config,
    } = state

    return {
        locale,
        isFetchingOrderHistory,
        isFetchingPastOrderHistory,
        fetchOrderHistoryError,
        fetchPastOrderHistoryError,
        pageSize,
        orders: {
            recent: getItemsWithPagination(orderHistory?.recentOrders, pageSize, messages, locale, config),
            past: getItemsWithPagination(orderHistory?.pastOrders, pageSize, messages, locale, config),
        },
    }
}

function getImageUrlFromTemplate(
    urlTemplate: string,
    partNumber: string,
    { imagesphereAspectRatio = '', width = 100 }: { imagesphereAspectRatio?: string; width?: number }
) {
    return urlTemplate
        ?.replace('{partNumber}', partNumber)
        .replace('{view}', 'in')
        .replace('{width}', `${width}${imagesphereAspectRatio}`)
}

function getProcessedPendingOrders(orders: RawPendingOrder[][] = [], locale, config) {
    const imagesphereAspectRatio = config?.head?.aspectRatio?.imagesphere || ''

    return orders.reduce((acc, orderPage) => {
        const processedOrders = orderPage.map((rawOrder): PendingOrder => {
            const { orderNumber, orderCompletion, currency, country, totalAmount, shippingMode, items = [] } = rawOrder
            const firstOrderItem = items[0]

            return {
                orderStatus: 'Pending',
                displayId: orderNumber,
                placedDate: getFormattedDate(orderCompletion, locale.id),
                orderDateRaw: orderCompletion,
                price: formatPrice(totalAmount, currency, locale.id),
                itemsNumber: items.length,
                shippingMethod: shippingMode,
                image:
                    firstOrderItem?.catalogEntryDetails?.legacyImageURL ||
                    getImageUrlFromTemplate(
                        firstOrderItem?.catalogEntryDetails?.imageTemplate,
                        firstOrderItem?.partNumber,
                        { imagesphereAspectRatio }
                    ),
                currencyCode: currency,
                countryCode: country,
            }
        })

        return [...acc, ...processedOrders]
    }, [] as PendingOrder[])
}

function getProcessedOrders(orders: RawOrder[][] = [], messages, locale, config) {
    const imagesphereAspectRatio = config?.head?.aspectRatio?.imagesphere || ''

    return orders.reduce((acc, orderPage) => {
        const processedOrders = orderPage.map((rawOrder): Order => {
            const {
                externalOrderID,
                derivedFromOrderNo,
                grandTotal: { value, currency },
                orderType,
                orderStatus,
                placedDate,
            } = rawOrder
            const firstOrderItem = rawOrder.itemDetails?.[0]

            const displayId =
                config?.head?.flags?.showExchangeDerivedOrderIds &&
                orderType?.toLowerCase() === 'exchange' &&
                derivedFromOrderNo
                    ? derivedFromOrderNo
                    : externalOrderID

            return {
                orderStatus: getOrderStatus(orderStatus, messages, orderType).status,
                externalOrderID,
                displayId,
                orderType,
                placedDate: getFormattedDate(placedDate, locale.id),
                orderDateRaw: placedDate,
                price: formatPrice(value.amount / value.divisor, currency.label, locale.id),
                viewDetailLink: `/${locale.id}/account/orders/${externalOrderID}`,
                itemsNumber: rawOrder.itemsNumber,
                trackingUrl: rawOrder.trackingUrl,
                shippingMethod: rawOrder.shippingMethodName,
                image:
                    firstOrderItem?.legacyImageURL ||
                    getImageUrlFromTemplate(firstOrderItem?.imageTemplate, firstOrderItem?.partNumber, {
                        imagesphereAspectRatio,
                    }),
                currencyCode: rawOrder.pricedInfoCurrency,
                countryCode: rawOrder.purchaseCountry,
                rawOrderStatus: orderStatus,
            }
        })

        return [...acc, ...processedOrders]
    }, [] as Order[])
}